import api from './api'
import type { EstadoIncidente } from '@/types/incidente'
import type { ResultadoAnonimizacao } from '@/types/privacidade'

export interface ConsentimentoResponse {
  versao: string | null
  aceito: boolean
  aceitoEm: string | null
  ip: string | null
  userAgent: string | null
}

export interface MeusDadosResponse {
  usuarioId: string
  perfil: {
    email: string
    nome: string
    ativo: boolean
    emailVerificado: boolean
  }
  atributos: Record<string, unknown>
  consentimento: ConsentimentoResponse
  geradoEm: string
}

export type SeveridadeIncidente = 'BAIXA' | 'MEDIA' | 'ALTA' | 'CRITICA'
export type StatusIncidente = 'ABERTO' | 'EM_ANALISE' | 'CONCLUIDO' | 'DESCARTADO'

export interface IncidenteRequest {
  severidade: SeveridadeIncidente
  titulo: string
  descricao: string
  titularesAfetados?: string
  acoesTomadas?: string
  contato?: string
  /** Lista curta da tela (Onda 7.6); opcional para não quebrar reportes antigos. */
  categoria?: string
  /** Ids de documentos já enviados ao originei-documentos (`tipoEntidade=INCIDENTE`). */
  anexos?: string[]
}

export interface Incidente extends IncidenteRequest {
  id: string
  protocolo: string
  status: StatusIncidente
  reportadoPor: string
  reportadoPorEmail?: string
  criadoEm: string
  tratadoEm?: string
  parecer?: string
  comunicadoAnpdEm?: string
  /** Estado da fila do DPO (Onda 7.6); o `status` antigo continua espelhado. */
  estado?: EstadoIncidente
  proximosEstados?: EstadoIncidente[]
  tenantId?: string | null
}

const BASE = '/api/v1/lgpd'

export const lgpdService = {
  versao: async (): Promise<string> => {
    const r = await api.get<{ versao: string }>(`${BASE}/versao`)
    return (r as unknown as { versao: string }).versao
  },

  meusDados: async (): Promise<MeusDadosResponse> => {
    return (await api.get<MeusDadosResponse>(`${BASE}/meus-dados`)) as unknown as MeusDadosResponse
  },

  exportarBlob: async (): Promise<Blob> => {
    const resp = await api.get(`${BASE}/exportar`, { responseType: 'blob' })
    return resp as unknown as Blob
  },

  /**
   * Autoanonimização (Art. 18, VI) — contrato do DEV-AA:
   *  - `LGPD_ANONIMIZACAO_IMEDIATA=true` (padrão, ONDA5 decisão 1): 200 `{ status: "anonimizado", mensagem }`;
   *  - `false` (AN-12 §4.3): 202 `{ status: "solicitada", protocolo, mensagem }` — nada é anonimizado na
   *    hora; a tela mostra o protocolo em vez de desconectar. Idempotente.
   * Corpo ausente (backend antigo) conta como imediata.
   */
  anonimizar: async (): Promise<ResultadoAnonimizacao> => {
    const r = await api.post<{ status?: string; protocolo?: string; mensagem?: string } | string | null | undefined>(
      `${BASE}/anonimizar`
    )
    const corpo: { status?: string; protocolo?: string; mensagem?: string } = r && typeof r === 'object' ? r : {}
    const protocolo = typeof corpo.protocolo === 'string' && corpo.protocolo ? corpo.protocolo : null
    const status = corpo.status === 'solicitada' || (protocolo && corpo.status !== 'anonimizado') ? 'solicitada' : 'anonimizado'
    return { status, protocolo, mensagem: corpo.mensagem }
  },

  meuConsentimento: async (): Promise<ConsentimentoResponse> => {
    return (await api.get<ConsentimentoResponse>(
      `${BASE}/consentimentos/meus`
    )) as unknown as ConsentimentoResponse
  },

  aceitar: async (versao: string): Promise<ConsentimentoResponse> => {
    return (await api.post<ConsentimentoResponse>(
      `${BASE}/consentimentos`,
      { versao }
    )) as unknown as ConsentimentoResponse
  },

  /**
   * Reporta incidente (Art. 48) — **persiste** e devolve protocolo. Até 15/08 a tela montava
   * um `mailto:`: quem não tivesse cliente de e-mail configurado não reportava, e não ficava
   * registro nem do incidente nem da tentativa.
   */
  reportarIncidente: async (payload: IncidenteRequest): Promise<Incidente> => {
    return (await api.post<Incidente>(`${BASE}/incidentes`, payload)) as unknown as Incidente
  },
}
